/**
 * @fileoverview Manufacturer Brand Cards - Handles click interactions and analytics tracking
 * for manufacturer brand cards
 *
 * This module manages user interactions with featured manufacturer brand cards on the page.
 * When a user clicks on a brand card, it:
 * 1. Tracks the event in Google Analytics 4
 * 2. Tracks the event in Segment
 * 3. Navigates the user to the brand's detail page
 *
 * Dependencies:
 * - Requires HTML elements with [data-offered-manufactured-brands] attribute
 * - Brand cards must have [data-brand-card] attribute
 * - Brand links must have [data-brand-link] attribute
 * - Brand cards must have data-index and data-brand-name attributes for analytics
 *
 * @module manufacturerBrandCards
 */
(function manufacturerBrandCards() {
  'use strict';

  const container = document.querySelector(
    '[data-offered-manufactured-brands]'
  );

  if (!container) {
    return;
  }

  /**
   * Extracts the brand page URL from a brand card element
   *
   * @param {HTMLElement} brandCard - The brand card element containing the brand link
   * @returns {string} The URL of the brand page, or an empty string if not found
   */
  const getBrandPageUrl = (brandCard) => {
    const brandUrlElement = brandCard.querySelector('[data-brand-link]');
    if (brandUrlElement) {
      return brandUrlElement.href;
    }
    return '';
  };

  /**
   * Navigates the browser to the brand page URL
   *
   * @param {HTMLElement} brandCard - The brand card element to extract the URL from
   */
  const openBrandPage = (brandCard) => {
    const url = getBrandPageUrl(brandCard);
    if (url) {
      window.location = url;
    }
  };

  /**
   * Tracks the brand card click event in Google Analytics 4
   *
   * Dispatches a custom event with GA4 tracking data including:
   * - Event name: 'detail_page_events'
   * - Click target: 'featured_brands_clicked'
   * - Brand position (1-indexed)
   * - Destination URL
   * - Brand name
   *
   * @param {HTMLElement} brandCard - The brand card element with dataset attributes
   */
  const trackGoogleAnalytics4Event = (brandCard) => {
    const dataset = brandCard.dataset;
    const url = getBrandPageUrl(brandCard);
    const parsedIndex = parseInt(dataset.index, 10) || 0;
    const eventGa4 = {
      eventName: 'detail_page_events',
      params: {
        click_target: 'featured_brands_clicked',
        position: parsedIndex + 1,
        destination: url,
        click_target1: dataset.brandName
      },
      eventType: 'GA4Event'
    };
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
      detail: { data: eventGa4 }
    }));
  };

  /**
   * Tracks the brand card click event in Segment
   *
   * Dispatches a custom event with Segment tracking data including:
   * - Event name: 'community_featured_brand_card_click'
   * - Brand name
   * - Brand position (1-indexed)
   * - Destination URL
   *
   * @param {HTMLElement} brandCard - The brand card element with dataset attributes
   */
  const trackSegmentEvent = (brandCard) => {
    const dataset = brandCard.dataset;
    const url = getBrandPageUrl(brandCard);
    const parsedIndex = parseInt(dataset.index, 10) || 0;
    const eventSegment = {
      event: 'community_featured_brand_card_click',
      eventType: 'segmentEvent',
      feature_brand_name: dataset.brandName,
      feature_brand_position: parsedIndex + 1,
      destination_url: url
    };
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', {
      detail: { data: eventSegment }
    }));
  };

  /**
   * Handles click events on brand cards
   *
   * This is the main event handler that orchestrates the click workflow:
   * 1. Finds the closest brand card element from the click target
   * 2. Tracks the interaction in Google Analytics 4
   * 3. Tracks the interaction in Segment
   * 4. Navigates to the brand page
   *
   * @param {Event} event - The click event object
   */
  const onClickBrandCard = (event) => {
    const brandCard = event.target.closest('[data-brand-card]');
    if (!brandCard) {
      return;
    }
    event.preventDefault();
    trackGoogleAnalytics4Event(brandCard);
    trackSegmentEvent(brandCard);
    setTimeout(() => openBrandPage(brandCard), 100);
  };

  // Set up event delegation on the container element
  container.addEventListener('click', onClickBrandCard);
}(window));
